import React, { useEffect, useRef } from 'react';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import { Event } from '../API';
import { parseAdditionalData } from '../utils/parseAdditionalData';

export interface Attendee {
    firstName?: string;
    lastName?: string;
    registrationId?: string;
    additionalData?: string;
    [key: string]: unknown;
}

interface ZplPrinterProps {
    attendee?: Attendee;
    eventInfo: Event;
    onPrinterReady?: (ready: boolean) => void;
    onPrintResult?: (result: { success: boolean; message: string }) => void;
}

/** Build ZPL label command string (unsanitized). */
const buildZpl = (attendee?: Attendee, _eventInfo?: Event): string => {
    const firstName = attendee?.firstName ?? 'Test';
    const lastName = attendee?.lastName ?? 'Print';
    const parsed = parseAdditionalData(attendee?.additionalData);
    const notes = typeof parsed.notes === 'string' ? parsed.notes : '';

    // Label size 57mm x 89mm, landscape orientation
    return `^XA
    ^MD10
    ^PR4
    ^FX === Label design v7
    ^FX LABEL SIZE 57mm x89mm
    ^FWB
    ^PW456
    ^LL710
    ^FX == Logo block
    ^FO55, 210
    ^GFA, 2610, 2610, 9,,:::::::::::::::::::: N038, N03F, N03FF, N01FFE, O01FE, P07E, O03FE, N01FF8, N03F,: N01FF, O07FE, P07E, P0FE, O0FFE, N03FF, N03F8, N038,, O0FFE, N01FFE, N03FFE, N03B8E, N03986, N031C6, N039C7, N038C7, N01CFE,: O0C7E, P038,, Q0E,::::::: M07IFE,:::,::::: M0738,: M07IFE, M03IFE,: N0IFE, N038,, O07F, O0FFC, N01FFC, N01E1E, N0380E, N03807,:: N0380E, N01E3E, N01FFC, O0FFC, O03F,,::::: M07IFE,:: M03FF7E,, O03E, O0FF8, N01FFC, N01FFE, N03C0E, N03807,::: N03C1E, N01FFE, O0FFC, O07F8, P08, P0C, O07F8, O0FFC, N01FFE, N01C1E, N0380E, N03807,:: N03C0E, N01FFE, N01FFC, O0FF8, O03F,, O03FE, O0FFE, N01FFE, N03FFE, N03C, N038,: N018, N01E, M07IFE,::,: O0E18, N01E1C, N01E1E, N03C0E, N03807,:: N03C0E, N01F7E, N01FFC, O0FF8, O03F,, P0F, N0E1FC, M01E3FC, M03E7FE, M07C78E, M07070F, M070F07,:: M070E0F, M079E0E, M03FE3E, M03FC3C, M01F83C, N0603,,::::::: Q02, Q0E, P07E, O03FE, N01FFE, N0IFE, M07IF8, L01IFE, L0JFE, K07FFE1E, K0FFE01E, K03FE01E, L0FFC1E, L03FF1E, M0IFE, M03FFE, N0FFE, N03FF, O0FFC, O03FE, O01FE, P07E, P01E, Q06,, P03E,::::::: K07EI03E, K07IFC3E, K07KFE,:: L0KFE, O0FFE,,: K038, K03F, K07EI0FC, K07CI07C, K07CI07E, K0FCI03E, K0FCI03F, K0FCI01F,: K07CI01F,: K07CI03F, K07EI03F, K03FI03E, K03F8007E, K01FC01FE, L0FFCFFC, L0KF8, L03JF8, L01IFE, M07FFC, N0FE,, K07F, K07IFC, K07JF8, K07JFE, K07KF, L07JF8, O0FFC, P0FE, P07E, P03E, P03F, P01F,:: P03F, K06J03F, K07FFC0FE, K07KFE, K07KFC, K07KF8, K07KF, M01FFC,,::::::::::::::::::::
    ^FS
    ^FX === Name block
    ^FB710, 2, 10, C
    ^FO180, 0
    ^A0B, 50, 42
    ^FD${firstName}${firstName.length + lastName.length > 39 ? '\\&' : ' '}${lastName}
    ^FS
    ^FX === Notes block
    ^FB610, 4, 10, C
    ^FO320, 60
    ^ADB, 15, 12
    ^FD ${notes || ""}
    ^FS
    ^XZ
    `;
};

/**
 * Sanitize ZPL string before sending to printer.
 * - Remove accidental spaces after '^' (e.g. '^ FX' -> '^FX')
 * - Remove non-printable / non-ASCII characters that can corrupt GFA data
 * - Normalize whitespace and trim each line
 */
const sanitizeZpl = (input: string): string => {
    if (!input) return '';
    let out = input;
    // Remove non-printable characters (keep tab, LF, CR and printable ASCII)
    out = out.replace(/[^\t\n\r\x20-\x7E]/g, '');
    // Remove spaces between '^' and command name
    out = out.replace(/\^\s+/g, '^');
    // Remove spaces after commas in ZPL arguments
    out = out.replace(/,\s+/g, ',');
    // Collapse multiple spaces to single
    out = out.replace(/ {2,}/g, ' ');
    // Trim each line
    out = out.split(/\r?\n/).map(l => l.trim()).join('\n');
    return out;
};

const ZplPrinter: React.FC<ZplPrinterProps> = ({ attendee, eventInfo, onPrinterReady, onPrintResult }) => {
    const printerRef = useRef<ZebraBrowserPrintWrapper | null>(null);
    const lastPrintedIdRef = useRef<string | null>(null);
    const lastPrintTimeRef = useRef<number>(0);

    // Store callbacks in refs so the effect doesn't re-trigger on every parent render
    const onPrinterReadyRef = useRef(onPrinterReady);
    const onPrintResultRef = useRef(onPrintResult);
    onPrinterReadyRef.current = onPrinterReady;
    onPrintResultRef.current = onPrintResult;

    useEffect(() => {
        if (!attendee) return;
        if (attendee.registrationId && attendee.registrationId === lastPrintedIdRef.current) return;

        // Claim this ID immediately to prevent duplicate prints from effect re-triggers
        lastPrintedIdRef.current = attendee.registrationId ?? null;

        let successTimeout: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        const setupAndPrint = async () => {
            try {
                let browserPrint = printerRef.current;
                if (!browserPrint) {
                    browserPrint = new ZebraBrowserPrintWrapper();
                    const defaultPrinter = await browserPrint.getDefaultPrinter();
                    if (!defaultPrinter) {
                        onPrinterReadyRef.current?.(false);
                        onPrintResultRef.current?.({ success: false, message: 'No Zebra printer found.' });
                        return;
                    }
                    browserPrint.setPrinter(defaultPrinter);
                    printerRef.current = browserPrint;
                }

                const printerStatus = await browserPrint.checkPrinterStatus();
                console.log('[ZplPrinter] Printer Status:', printerStatus);

                if (printerStatus.isReadyToPrint) {
                    // Brief delay in case printer is transitioning
                    await new Promise(res => setTimeout(res, 500));
                    if (cancelled) return;

                    const rawZpl = buildZpl(attendee, eventInfo);
                    const zpl = sanitizeZpl(rawZpl);
                    console.log('[ZplPrinter] Sending ZPL to printer:\n', zpl);
                    await browserPrint.print(zpl);

                    lastPrintTimeRef.current = Date.now();
                    onPrinterReadyRef.current?.(true);

                    // Delay success feedback — an "Unknown Error" may follow immediately
                    successTimeout = setTimeout(() => {
                        if (!cancelled) {
                            onPrintResultRef.current?.({ success: true, message: 'Label sent to printer!' });
                        }
                    }, 3000);
                } else {
                    onPrinterReadyRef.current?.(false);
                    onPrintResultRef.current?.({ success: false, message: `Printer not ready: ${printerStatus.errors}` });
                }
            } catch (err: unknown) {
                console.error('[ZplPrinter] Error during print:', err);

                const errMsg = err && typeof err === 'object' && 'message' in err
                    ? String((err as { message: unknown }).message)
                    : '';

                // Suppress "Unknown Error" if it occurs within 5s of a successful print
                if (errMsg.includes('Unknown Error') && Date.now() - lastPrintTimeRef.current < 5000) {
                    if (successTimeout) clearTimeout(successTimeout);
                    return;
                }

                let friendlyMessage = 'Error printing label.';
                if (errMsg.includes('Unknown Error')) {
                    friendlyMessage = 'Printer is initializing or not ready. Please wait a moment and try again.';
                } else if (errMsg) {
                    friendlyMessage = `Printer error: ${errMsg}`;
                }

                if (successTimeout) clearTimeout(successTimeout);
                onPrinterReadyRef.current?.(false);
                onPrintResultRef.current?.({ success: false, message: friendlyMessage });
            }
        };

        void setupAndPrint();

        return () => {
            cancelled = true;
            if (successTimeout) clearTimeout(successTimeout);
        };
    }, [attendee, eventInfo]);

    return null;
};

export default ZplPrinter;
